/**
 * CompletionSettingsForm Component
 *
 * When a document counts as completed — the statuses it has to reach, plus delivery and
 * invoicing — and, in a section of its own, whether and when a completed one is archived.
 * Every choice is stored the moment it changes; the days when the field is left.
 */
import { Paper } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { ArchivingOnCompletionSection } from './ArchivingOnCompletionSection';
import { CompletionConditionToggle } from './CompletionConditionToggle';
import { baseStatusAfterSwitch, isBaseStatusSwitchOn } from './completion-base-status';

/** The completion settings of one document kind. */
export interface CompletionSettingsValue<TBaseStatus extends string> {
  completionBaseStatus: TBaseStatus;
  isDeliveryRequired: boolean;
  isInvoicingRequired: boolean;
  isArchivingOnCompletion: boolean;
  archiveAfterDays: number;
}

export interface CompletionSettingsFormProps<TBaseStatus extends string> {
  /** Translation namespace under `systemSettings.automation`. */
  namespace: 'purchaseOrderClosure' | 'requisitionClosure';
  /** Statuses completion may start from, in lifecycle order; the first is the minimum. */
  baseStatuses: readonly TBaseStatus[];
  value: CompletionSettingsValue<TBaseStatus>;
  onChange: (change: Partial<CompletionSettingsValue<TBaseStatus>>) => void;
  /** Locks every switch and the archiving days, e.g. for whoever may only read the settings. */
  disabled?: boolean;
  /** Prefix of every control's test id. */
  testIdPrefix: string;
}

export function CompletionSettingsForm<TBaseStatus extends string>({
  namespace,
  baseStatuses,
  value,
  onChange,
  disabled = false,
  testIdPrefix
}: CompletionSettingsFormProps<TBaseStatus>) {
  const t = useFeatureTranslation(`systemSettings.automation.${namespace}`);

  return (
    <div className='space-y-lg'>
      <Paper heading='stripe' bleed padding='none' testId={`${testIdPrefix}-conditions-section`}>
        <Paper.Title>
          <h2>{t('title')}</h2>
        </Paper.Title>
        <Paper.Content className='space-y-md'>
          <p className='text-sm text-foreground-muted'>{t('description')}</p>
          <div className='space-y-md'>
            {baseStatuses.map((status, index) => (
              <CompletionConditionToggle
                key={status}
                label={t(`baseStatus.${status}`)}
                checked={isBaseStatusSwitchOn(baseStatuses, value.completionBaseStatus, index)}
                // The first status is the minimum the chain allows, so it is always on.
                disabled={disabled || index === 0}
                onChange={isOn =>
                  onChange({
                    completionBaseStatus: baseStatusAfterSwitch(baseStatuses, index, isOn)
                  })
                }
                testId={`${testIdPrefix}-base-status-${status}`}
              />
            ))}
            <CompletionConditionToggle
              label={t('delivery.label')}
              description={t('delivery.description')}
              checked={value.isDeliveryRequired}
              onChange={isDeliveryRequired => onChange({ isDeliveryRequired })}
              disabled={disabled}
              testId={`${testIdPrefix}-delivery`}
            />
            <CompletionConditionToggle
              label={t('invoicing.label')}
              description={t('invoicing.description')}
              checked={value.isInvoicingRequired}
              onChange={isInvoicingRequired => onChange({ isInvoicingRequired })}
              disabled={disabled}
              testId={`${testIdPrefix}-invoicing`}
            />
          </div>
        </Paper.Content>
      </Paper>

      <ArchivingOnCompletionSection
        namespace={namespace}
        checked={value.isArchivingOnCompletion}
        onChange={isArchivingOnCompletion => onChange({ isArchivingOnCompletion })}
        archiveAfterDays={value.archiveAfterDays}
        onArchiveAfterDaysChange={archiveAfterDays => onChange({ archiveAfterDays })}
        disabled={disabled}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
}
